import { LegalShell as Layout } from "@/components/connect/LegalShell";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { MARKETING_CONFIG } from "@/config/marketing";
import {
  ArrowLeft,
  Shield,
  Building2,
  Phone,
  Globe,
  FileText,
  AlertTriangle,
  Scale,
  Users,
  Lock,
  Accessibility,
} from "lucide-react";

/**
 * Regulatory Information for elefa.
 *
 * Truthful, conservative posture for a Melbourne-based OTT VoIP service that:
 *   - Is NOT a carrier (does not own infrastructure / no carrier licence)
 *   - Is NOT a TIO member (yet)
 *   - Holds NO regulatory registrations outside Australia
 *   - Does NOT issue Australian numbers, so no IPND obligation
 *   - Does NOT provide a Standard Telephone Service (no CSG)
 *   - CANNOT call emergency services
 *
 * Anything the company plans to do in future is intentionally NOT mentioned
 * here - this page describes only what is true today.
 */
const RegulatoryInformation = () => {
  const c = MARKETING_CONFIG.company;

  return (
    <Layout>
      <SEOHead
        title={PAGE_SEO.regulatory.title}
        description={PAGE_SEO.regulatory.description}
        keywords={PAGE_SEO.regulatory.keywords}
        canonicalUrl={getCanonicalUrl('/regulatory')}
        structuredData={getBreadcrumbSchema([
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: 'Regulatory Information', url: 'https://elefa.tel/regulatory' },
        ])}
      />
      <section className="py-12">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            {/* Back */}
            <div className="mb-8">
              <Link to="/support">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Support
                </Button>
              </Link>
            </div>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Regulatory Information
              </h1>
              <p className="text-muted-foreground">
                elefa is an over-the-top (OTT) Voice-over-IP service operated
                from Melbourne, Australia. This page describes what we are, what we
                are not, and the rules we operate under.
              </p>
            </div>

            {/* Company Details */}
            <Card className="mb-8 border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="h-5 w-5 text-primary" />
                  Company Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Legal Name</p>
                    <p className="font-semibold text-foreground">{c.legalName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Trading As</p>
                    <p className="font-semibold text-foreground">elefa</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">ABN</p>
                    <p className="font-semibold text-foreground">{c.abn}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">ACN</p>
                    <p className="font-semibold text-foreground">{c.acn}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Country of incorporation</p>
                    <p className="font-semibold text-foreground">Australia</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-8">
              {/* What we are */}
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" />
                  What elefa is
                </h2>
                <Card>
                  <CardContent className="pt-6 space-y-3 text-sm text-muted-foreground">
                    <p>
                      elefa is an over-the-top (OTT) Voice-over-IP application.
                      You make outbound calls over the public internet using
                      your existing data connection. We do not provide you with
                      a mobile or fixed-line service, and we do not issue you
                      a phone number.
                    </p>
                    <p>
                      Outbound calls to ordinary phone numbers (mobile or
                      landline, including phones with no internet connection)
                      are carried over the Public Switched Telephone Network
                      (PSTN) by wholesale interconnect carriers. All traffic
                      between your device and our servers is encrypted in
                      transit (TLS/SRTP); the PSTN leg cannot be end-to-end
                      encrypted by its nature.
                    </p>
                    <p>
                      <strong>In-app elefa-to-elefa calling, messaging and
                      collaboration features are not currently offered.</strong>{" "}
                      They are on our future roadmap; if and when released,
                      end-to-end encryption is planned at launch and this page
                      will be updated.
                    </p>
                    <p>
                      "elefa" (lowercase) is the trading name of {c.legalName}.
                      The word "Connect" sometimes appears alongside the elefa
                      name in marketing material; it is a marketing label only
                      and is not a separate legal entity or product.
                    </p>
                  </CardContent>
                </Card>

              </section>

              {/* Australian regulatory posture */}
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Scale className="h-5 w-5 text-primary" />
                  Australian regulatory posture
                </h2>
                <div className="space-y-4">
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-foreground mb-2">Carriage Service Provider (CSP)</h3>
                      <p className="text-sm text-muted-foreground">
                        Under the Telecommunications Act 1997 (Cth), we operate as a
                        Carriage Service Provider - that is, a person who supplies
                        carriage services to the public using infrastructure operated by
                        licensed carriers. <strong>CSPs are not licensed by ACMA;</strong>{" "}
                        only carriers that own network infrastructure require a carrier
                        licence. We are not a carrier.
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-foreground mb-2">Privacy and telecommunications information</h3>
                      <p className="text-sm text-muted-foreground">
                        We are bound by the Privacy Act 1988 (Cth) and the Australian
                        Privacy Principles. When we handle "telecommunications
                        information" (the fact, time or content of a communication) we
                        are also bound by Part 13 of the Telecommunications Act 1997.
                        See our{" "}
                        <Link to="/legal/privacy" className="text-primary hover:underline">
                          Privacy Policy
                        </Link>{" "}
                        for the full detail.
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-foreground mb-2">Telecommunications Consumer Protections (TCP) Code</h3>
                      <p className="text-sm text-muted-foreground">
                        We apply the consumer-facing principles of the TCP Code
                        C628:2019 - clear advertising, transparent pricing shown before
                        every call, billing accuracy, and structured complaint handling
                        within the timeframes the Code sets out. The Code&apos;s formal
                        scope is directed at carriers and CSPs of standard telephone
                        services; we apply equivalent standards to our OTT service.
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-foreground mb-2">Spam Act and Do Not Call Register</h3>
                      <p className="text-sm text-muted-foreground">
                        Marketing emails and SMS are sent only with consent and always
                        with a clear unsubscribe mechanism, in accordance with the Spam
                        Act 2003 (Cth). We do not place outbound marketing telephone
                        calls.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </section>

              {/* What we are NOT */}
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-warning" />
                  What elefa is not
                </h2>
                <Card>
                  <CardContent className="pt-6 space-y-3 text-sm text-muted-foreground">
                    <p>
                      To avoid any consumer confusion, we want to be explicit about
                      what elefa <strong>does not</strong> do today:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>It is not a "standard telephone service" and the Customer Service Guarantee Standard (Telstra Standard) does not apply.</li>
                      <li>It does not issue you an Australian phone number, so we make no submissions to the Integrated Public Number Database (IPND).</li>
                      <li>It is not a mobile, fixed-line or NBN service. There is no SIM, no eSIM, no number porting and no data plan.</li>
                      <li>It is not a carrier or carriage service intermediary requiring a carrier licence.</li>
                      <li>elefa Mobile Pty Ltd is not currently a member of the Telecommunications Industry Ombudsman (TIO) scheme. Membership will be confirmed at the appropriate regulatory threshold.</li>
                      <li>We hold no licences, registrations or local entities in any country other than Australia.</li>
                    </ul>
                  </CardContent>
                </Card>
              </section>

              {/* Emergency Services */}
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Emergency services
                </h2>
                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-destructive mb-2">
                      elefa cannot be used to call emergency services.
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Triple Zero (000) in Australia, 112 in the EU/UK and many other
                      countries, 911 in the US/Canada, 999 in the UK and parts of Africa
                      and Asia, 111 in New Zealand and all equivalent local emergency
                      numbers are <strong>not supported</strong>. The Service will not
                      transmit your physical location to emergency answer points, may
                      not work during a power or internet outage, and may not connect
                      at all. You must always maintain access to an alternative means of
                      calling emergency services.
                    </p>
                  </CardContent>
                </Card>
              </section>

              {/* International users */}
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" />
                  International users
                </h2>
                <Card>
                  <CardContent className="pt-6 space-y-3 text-sm text-muted-foreground">
                    <p>
                      Although the Service is offered globally, elefa is established and
                      regulated only in Australia. We do not hold a local licence,
                      registration or representative entity in any other country, and we
                      have not appointed an EU or UK GDPR Article 27 representative.
                      Where you reside outside Australia, your local consumer-protection
                      and data-protection laws may give you additional non-excludable
                      rights - nothing in our policies is intended to displace those
                      rights.
                    </p>
                    <p>
                      Some countries restrict, license or ban OTT VoIP services like
                      elefa. Before you install or use the Service from outside
                      Australia, please read our{" "}
                      <Link to="/legal/service-availability" className="text-primary hover:underline">
                        Service Availability &amp; Country Restrictions
                      </Link>{" "}
                      page.
                    </p>
                  </CardContent>
                </Card>
              </section>

              {/* Accessibility */}
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Accessibility className="h-5 w-5 text-primary" />
                  Accessibility
                </h2>
                <Card>
                  <CardContent className="pt-6 text-sm text-muted-foreground">
                    <p>
                      Australian users with hearing or speech impairment can contact us
                      via the National Relay Service (NRS) at{" "}
                      <a href="https://accesshub.gov.au/about-the-nrs" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        accesshub.gov.au/about-the-nrs
                      </a>. The NRS is a free service funded by the Australian Government.
                    </p>
                  </CardContent>
                </Card>
              </section>

              {/* Consumer protections */}
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Consumer-facing documents
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Scale className="h-4 w-4 text-primary" />
                        Fair Use Policy
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        Acceptable-use rules for the elefa calling service.
                      </p>
                      <Link to="/legal/fair-use" className="text-sm text-primary hover:underline">
                        Read the Fair Use Policy →
                      </Link>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        Financial Hardship
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        Help for Australian customers facing financial difficulty.
                      </p>
                      <Link to="/legal/financial-hardship" className="text-sm text-primary hover:underline">
                        Read the Hardship Policy →
                      </Link>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        Complaints
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        How to raise and escalate a complaint with us.
                      </p>
                      <Link to="/legal/complaints" className="text-sm text-primary hover:underline">
                        Read the Complaints Policy →
                      </Link>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Lock className="h-4 w-4 text-primary" />
                        Privacy &amp; Data
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        How we handle your personal information.
                      </p>
                      <Link to="/legal/privacy" className="text-sm text-primary hover:underline">
                        Read the Privacy Policy →
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </section>

              {/* External escalation */}
              <section>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Phone className="h-5 w-5 text-primary" />
                  External escalation
                </h2>
                <Card>
                  <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
                    <div>
                      <p className="font-semibold text-foreground mb-1">Office of the Australian Information Commissioner (OAIC)</p>
                      <p>For privacy complaints we have not resolved: <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">oaic.gov.au</a> · 1300 363 992</p>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-1">Australian Competition and Consumer Commission (ACCC)</p>
                      <p>For consumer-law complaints: <a href="https://www.accc.gov.au" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">accc.gov.au</a></p>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-1">eSafety Commissioner</p>
                      <p>For online safety concerns: <a href="https://www.esafety.gov.au" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">esafety.gov.au</a></p>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-1">Telecommunications Industry Ombudsman (TIO)</p>
                      <p>
                        elefa Mobile Pty Ltd is not currently a TIO member, so the TIO
                        cannot adjudicate disputes against us at this time. We will
                        register at the regulatory threshold that applies to our
                        Service. In the meantime, unresolved telecommunications
                        complaints may still be raised with the OAIC or ACCC as
                        appropriate.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </section>
            </div>

            <div className="mt-12 flex flex-wrap gap-4 text-sm">
              <Link to="/legal/terms" className="text-primary hover:underline">Terms of Service</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/legal/privacy" className="text-primary hover:underline">Privacy Policy</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/legal/service-availability" className="text-primary hover:underline">Service Availability</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RegulatoryInformation;
